import { AnimatedSprite, Sprite, Ticker } from "pixi.js"
import { predictedFallAirborne, predictedFallPending } from "../../../../predicted_fall"
import { isAirborne } from "../effects/floor_fall"
import { Entity } from "game-common/models"
import { LpcJobResult } from "./lpc_composite_generator"
import { AnimationDictionaryType, HeldPose, LpcCompositeSprite, LpcSpriteRenderMeta } from "./lpc_composite_sprite"
import { BakedEmberAnimation, emberFrameBaker } from "./ember_frame_baker"
import {
    EmberEquipment,
    EmberJoints,
    MOVING_ATTACK_GAITS,
    aimedShotName,
    aimsShots,
    movingAttackName,
    parseAnimationName,
    planEmberAnimation,
    heldFrame,
} from "game-common/ember_sprite/generate"
import { IDLE_VARIATIONS, isIdleVariation, THREATEN_ACTIONS, CLIMB_RISE, EMBER_CLIPS, LPC_ACTION_DURATION_MS } from "game-common/ember_sprite/clips"
import { FROM_REST, SETTLE, STANCE_ATTACKS } from "game-common/ember_sprite/settle"
import { BodyAnimationPose } from "../effects/animation_pose"
import { ladderRiseOf } from "../effects/stair_travel"
import { heldObjectTexture } from "./lpc_held_object"
import { WalkAnimationPace } from "./walk_animation_pace"

/** How big a carried load is drawn: its longer side, in pixels (LPC draws its held objects at 18). */
export const EMBER_LOAD_SIZE = 16
import { Facing, emberFacingAt, isBackFacing } from "game-common/ember_sprite/raster"
import { EMBER_HUMANOIDS } from "game-common/ember_sprite/humanoids"
import { JUMP_FLOURISH } from "game-common/animation_pose"
import { JUMP_HOLD, JUMP_LANDING, JUMP_MS } from "game-common/jump"

/**
 * For a body with no run key (an NPC): above this measured ground speed (px/s) it runs; it walks
 * again below the lower one. A player's gait is the run key instead (see EmberCompositeSprite.runKey).
 */
export const RUN_ABOVE = 78
export const WALK_BELOW = 66

/** The LPC body frame every Ember frame centres its body in. */
const BODY_FRAME = 64

/** How long a stopped stride holds its frame for its settle to bake before it gives up and stands. */
export const SETTLE_WAIT_MS = 300

/** PIXI plays `animationSpeed` frames per 60Hz tick. */
const TICKS_PER_SECOND = 60

/** How often a standing humanoid chooses whether to play an idle gesture. */
const IDLE_CHOICE_MS = 2000

/**
 * Frames per tick that keep a gait's planted feet still on the ground: the clip covers
 * `strideCycle` pixels per cycle, so at `speed` px/s it must cycle speed / strideCycle times a second.
 */
export const gaitAnimationSpeed = (speed: number, strideCycle: number, frames: number): number =>
    (speed / strideCycle) * (frames / TICKS_PER_SECOND)

/**
 * A body drawn from a generated Ember humanoid instead of LPC sheets.
 *
 * Everything around the frames -- shadow, rings, halos, hover outline, held poses, attack
 * callbacks, the death shatter -- is LpcCompositeSprite's, unchanged. What differs is where an
 * action's frames come from (rendered from the reference skeleton, see game-common/ember_sprite),
 * that the body can RUN as well as walk -- a player while holding the run key (Shift), so the gait is
 * the same on every client -- and that a gait is timed from the ground it covers rather
 * than from a tuned multiplier: the clip knows its stride exactly, so the feet never slide.
 *
 * It also STRIKES ON THE MOVE: an attack started while walking or running plays the attack's
 * upper body over that gait's legs, from where the stride had got to, and hands back to the gait
 * where the strike left the legs -- instead of LPC's standing swing sliding across the ground.
 *
 * And a player's body COMES TO REST (game-common/ember_sprite/settle.ts) instead of cutting to its
 * standing frame: stopping, it settles from the frame its stride was on; a blow struck from standing
 * eases out of the standing pose and settles back into it afterwards. A settle bakes the first
 * time it is wanted, the stride holding its frame meanwhile. NPCs still cut: every look would bake
 * its own settles, and there are many more of them.
 */
export class EmberCompositeSprite extends LpcCompositeSprite {
    protected walkPace = new WalkAnimationPace(0)
    readonly spriteName: string
    /** What the body holds, drawn into every frame; a change of equipment builds a new sprite. */
    readonly equipment: EmberEquipment
    private nextIdleChoice = Date.now() + IDLE_CHOICE_MS
    private pendingIdle?: AnimationDictionaryType
    private running = false
    /**
     * A PLAYER runs while the server has them running (Entity.boosted: Shift held, see
     * Message.boosted) and walks otherwise, however fast they are going. Undefined for a body with
     * no run key, which chooses by its speed instead.
     */
    private runKey?: boolean
    private baked = new Map<string, BakedEmberAnimation>()
    /** Set as a strike on the move starts: the gait to hand back to, and the strike it follows. */
    private resumeGait?: { action: AnimationDictionaryType; after: AnimationDictionaryType }
    /** The screen heading the next shot flies along (aimShot), for a body that aims its shots. */
    private shotHeading?: number
    private readonly standingPostAnimation = this.postAnimation
    /** Comes to rest through settles rather than cutting to idle: a player's humanoid body. */
    private settles = false
    /**
     * The settle a stopped stride is waiting on to bake. The stride holds its frame meanwhile -- the
     * settle starts from that frame -- and picks up again if the body moves first.
     */
    private pendingSettle?: { name: AnimationDictionaryType; stride: AnimationDictionaryType; since: number }
    /** The facing a jump is drawn in, taken as it leaves the ground and kept until it is down. */
    private jumpFacing?: Facing
    /** The leap's drop the body is in (FloorFallView.leap), and when the last one landed. */
    private leapFallId?: string
    private leapLandedAt?: number
    /** Facings a player's body has had its jump drawn for ahead of time, so the first one is not late. */
    private readonly jumpsBaked = new Set<string>()
    /** The item carried in the arms (EmberEquipment.carry): its art, between the hands, frame by frame. */
    private readonly loadSpriteId?: string
    private load?: Sprite

    constructor(spriteName: string, meta: LpcSpriteRenderMeta, equipment: EmberEquipment = {}) {
        // No sheets to load, and no LPC held-object art baked in: it is placed against LPC hands. A
        // held weapon is generated into the frames instead (game-common/ember_sprite, EMBER_HELD); a
        // carried item is the item's own art, held between the Ember body's hands (updateLoad).
        super({ ...meta, descriptors: [], heldObject: undefined })
        this.spriteName = spriteName
        this.equipment = equipment
        this.loadSpriteId = equipment.carry ? meta.heldObject?.spriteId : undefined
        if (this.loadSpriteId) Ticker.shared.add(this.updateLoad)
    }

    /**
     * The carried item, kept between the hands of the frame on show (its baked joints) -- so it rises
     * and falls with the arms on the move -- in front of the body, or behind it facing away (where
     * LPC puts its held objects too), tinted as the body is. Shown only while the arms are holding
     * it: an attack, a cast or a climb puts it out of sight.
     */
    private updateLoad = () => {
        if (!this.loadSpriteId || !this.initialized || !this.container) return
        const job = this.animations[this.action]
        const animation = job?.animation
        const holding = !!this.baked.get(this.action)?.carrying && !!animation?.visible
        if (!this.load) {
            if (!holding) return
            const texture = heldObjectTexture(this.loadSpriteId)
            if (!texture) return
            this.load = new Sprite(texture)
            // Resting on the forearms: centred over the hands, most of it above them.
            this.load.anchor.set(0.5, 0.75)
            this.load.scale.set(EMBER_LOAD_SIZE / Math.max(texture.orig.width, texture.orig.height))
        }
        const load = this.load
        const hands = holding ? this.jointLocal(j => ({ x: (j.hands[0].x + j.hands[1].x) / 2, y: (j.hands[0].y + j.hands[1].y) / 2 })) : undefined
        load.visible = !!hands
        if (!hands || !animation) return
        load.position.set(hands.x, hands.y)
        load.tint = animation.tint
        const behind = isBackFacing(parseAnimationName(this.action)?.facing ?? this.facingDir)
        const container = this.container
        const at = load.parent === container ? container.getChildIndex(load) : -1
        const body = container.getChildIndex(animation)
        if (at < 0 || (behind ? at !== body - 1 : at !== body + 1)) {
            if (at >= 0) container.removeChild(load)
            const index = container.getChildIndex(animation)
            container.addChildAt(load, behind ? index : index + 1)
        }
    }

    remove() {
        Ticker.shared.remove(this.updateLoad)
        super.remove()
    }

    destroy() {
        Ticker.shared.remove(this.updateLoad)
        super.destroy()
    }

    protected requestAnimation(action: AnimationDictionaryType, callback: (result: LpcJobResult | null) => void): void {
        emberFrameBaker.request(
            this.spriteName,
            action,
            this.priority,
            baked => {
                if (!baked) {
                    callback(null)
                    this.settleFailed(action)
                    return
                }
                this.baked.set(action, baked)
                const animation = new AnimatedSprite(baked.textures)
                animation.visible = false
                animation.animationSpeed = 1000 / (baked.frameMs * TICKS_PER_SECOND)
                // A held tool's frames are bigger than the 64px body frame and centre the same
                // body: shifted back by the margin, as LPC's oversize sheets are, the body stays put.
                const result = new LpcJobResult({ animation })
                result.offsetX = -(baked.textures[0].width - BODY_FRAME) / 2
                result.offsetY = -(baked.textures[0].height - BODY_FRAME) / 2
                animation.position.set(result.offsetX, result.offsetY)
                callback(result)
            },
            () => this.spriteReleased,
            this.equipment,
        )
        // A blow struck standing settles afterwards: have the settle baked by the time the blow ends.
        const parsed = parseAnimationName(action)
        if (parsed && !parsed.gait && this.settlesAttack(parsed.action)) {
            emberFrameBaker.request(this.spriteName, `${SETTLE}.${parsed.action}-${parsed.facing}`, false, () => {}, () => this.spriteReleased, this.equipment)
        }
    }

    /** A settle that could not be drawn (no plan for it): stand, rather than wait on it. */
    private settleFailed = (action: AnimationDictionaryType) => {
        if (parseAnimationName(action)?.action !== SETTLE) return
        const pending = this.pendingSettle?.name === action
        if (pending) this.pendingSettle = undefined
        if (pending || this.awaits(action)) this.activateAnimation(`idle-${this.facingDir}` as AnimationDictionaryType)
    }

    /** A blow struck standing, with this body: eased into from standing and settled out of. */
    private settlesAttack = (action: string): boolean => this.settles && STANCE_ATTACKS.includes(action) && !this.equipment.carry

    /** The blow this settle follows has played out and is waiting on it. */
    private awaits = (settle: AnimationDictionaryType): boolean => {
        const parsed = parseAnimationName(settle)
        const current = parseAnimationName(this.action)
        if (!parsed?.after || !current || current.gait || current.action !== parsed.after || current.facing !== parsed.facing) return false
        return !this.animations[this.action]?.animation?.playing
    }

    /**
     * Where the current frame puts a joint, in this sprite's own space: through the frame as it is
     * drawn now -- offset when bigger, and lifted off the ground by a jump or a flourish -- so what
     * hangs off a joint (a carried load, a halo) goes where the body goes.
     */
    private jointLocal = (pick: (joints: EmberJoints) => { x: number; y: number }): { x: number; y: number } | undefined => {
        const baked = this.baked.get(this.action)
        const animation = this.animations[this.action]?.animation
        const joints = baked?.joints[animation?.currentFrame ?? 0]
        if (!baked || !joints || !this.initialized) return undefined
        const point = pick(joints)
        if (!animation) {
            // An Ember body is placed at the origin (lpc_composite), so only a bigger frame's growth moves it.
            const grow = (baked.textures[0].width - BODY_FRAME) / 2
            return { x: point.x - grow, y: point.y - grow }
        }
        animation.transform.updateLocalTransform()
        const at = animation.localTransform.apply({
            x: point.x - animation.anchor.x * animation.texture.width,
            y: point.y - animation.anchor.y * animation.texture.height,
        })
        return { x: at.x, y: at.y }
    }

    /** The head joint of the frame on show, in this sprite's own space. */
    headLocal = () => this.jointLocal(j => j.head)

    /** Top of the visible hair/head, in body space, following the current frame's transforms. */
    headTopLocal = () => {
        const animation = this.animations[this.action]?.animation
        const top = this.baked.get(this.action)?.headTops?.[animation?.currentFrame ?? 0]
        return top === undefined ? undefined : this.jointLocal(j => ({ x: j.head.x, y: top }))
    }

    /** Bow grip in body space; late remote shots use the release pose rather than an idle hand. */
    arrowGripLocal(angle: number): { x: number; y: number } | undefined {
        if (this.equipment.held !== "bow") return undefined
        const facing = this.calcDir(angle)
        if (parseAnimationName(this.action)?.action === "shoot" && this.facingDir === facing) {
            const grip = this.jointLocal(j => j.hands[0])
            if (grip) return grip
        }
        const plan = planEmberAnimation(this.spriteName, `shoot-${facing}`, this.equipment)
        if (!plan) return undefined
        const grip = plan.jointsAt(Math.floor(plan.frameCount / 2)).hands[0]
        // The plan centres the 64px body in its larger weapon frame, just like the baked art.
        const bodyGrowth = (EMBER_HUMANOIDS[this.spriteName].frame ?? BODY_FRAME) - BODY_FRAME
        const grow = (heldFrame("bow") + bodyGrowth - BODY_FRAME) / 2
        return { x: grip.x - grow, y: grip.y - grow }
    }

    /**
     * The skull a halo or a skeleton's effects hang from: the Ember body's own head, frame by frame,
     * where the LPC sprite reads a track measured off LPC's skeleton sheet.
     */
    headAttachmentWorld = (race: string) => {
        if (race !== "skeleton") return undefined
        const head = this.jointLocal(j => j.head)
        return head && this.toGlobal(head)
    }

    /** A skeleton's grip for what its attack holds or casts: between its raised hands in a cast, else its right fist. */
    heldAttackGripWorld = (race: string) => {
        if (race !== "skeleton") return undefined
        const casting = this.action.startsWith("cast-")
        const grip = this.jointLocal(j => (casting ? { x: (j.hands[0].x + j.hands[1].x) / 2, y: (j.hands[0].y + j.hands[1].y) / 2 } : j.hands[1]))
        if (!grip) return undefined
        const hands = casting ? [0, 1].map(i => this.toGlobal(this.jointLocal(j => j.hands[i])!)) : undefined
        const world = this.toGlobal(grip)
        return { x: world.x, y: world.y, scale: Math.hypot(this.worldTransform.a, this.worldTransform.b), hands }
    }

    calcDir(angle: number): Facing {
        return emberFacingAt(angle)
    }

    doMovement(entity: Entity): void {
        this.runKey = entity.entityType === "player" ? entity.boosted === true : undefined
        this.settles = entity.entityType === "player" && !!EMBER_HUMANOIDS[this.spriteName]
        // A player can jump at any moment: have the jump for the way it faces drawn before it does.
        if (this.settles && this.initialized && entity.movement) {
            const facing = this.calcDir(entity.movement.angle)
            if (!this.jumpsBaked.has(facing)) {
                this.jumpsBaked.add(facing)
                emberFrameBaker.request(this.spriteName, `jump-${facing}`, false, () => {}, () => this.spriteReleased, this.equipment)
            }
        }
        // Moving again before the settle it was waiting on has baked: pick the stride back up.
        const at = entity.location
        if (this.pendingSettle && at && this.lastX !== undefined && (at.x !== this.lastX || at.y !== this.lastY)) this.resumeStride()
        const action = parseAnimationName(this.action)?.action ?? ""
        if ((!this.action.startsWith("idle-") && !isIdleVariation(action)) ||
            (at && this.lastX !== undefined && (at.x !== this.lastX || at.y !== this.lastY))) {
            this.nextIdleChoice = Date.now() + IDLE_CHOICE_MS
            this.pendingIdle = undefined
        }
        super.doMovement(entity)
    }

    private resumeStride = () => {
        const pending = this.pendingSettle
        this.pendingSettle = undefined
        if (pending && this.action === pending.stride) this.animations[this.action]?.animation?.play()
    }

    /**
     * Stopped: settle from the stride's frame (the next even one -- settles are drawn from every
     * other frame). Until that settle has baked the stride holds its frame, and gives up and stands
     * after SETTLE_WAIT_MS. A settle under way is left to finish: it hands over to idle itself.
     */
    protected restAnimation(): AnimationDictionaryType {
        const idle = super.restAnimation()
        const current = parseAnimationName(this.action)
        if (current && isIdleVariation(current.action)) return this.action
        if (this.action.startsWith("idle-") && EMBER_HUMANOIDS[this.spriteName] && Date.now() >= this.nextIdleChoice) {
            this.nextIdleChoice = Date.now() + IDLE_CHOICE_MS
            const choice = Math.random()
            this.pendingIdle = choice < 0.5 ? undefined : `${IDLE_VARIATIONS[Math.min(2, Math.floor((choice - 0.5) * 6))]}-${this.facingDir}` as AnimationDictionaryType
            return this.pendingIdle ?? idle
        }
        if (current?.action === SETTLE) return this.action
        if (!this.settles || !current || current.gait || !MOVING_ATTACK_GAITS.includes(current.action)) return idle
        const pending = this.pendingSettle
        if (pending?.stride === this.action) {
            if (Date.now() - pending.since <= SETTLE_WAIT_MS) return pending.name
            this.pendingSettle = undefined
            return idle
        }
        const stride = this.animations[this.action]?.animation
        if (!stride) return idle
        const frame = (2 * Math.ceil(stride.currentFrame / 2)) % stride.totalFrames
        const name = `${SETTLE}.${current.action}.${frame}-${current.facing}` as AnimationDictionaryType
        if (this.animations[name]) return name
        stride.stop()
        this.pendingSettle = { name, stride: this.action, since: Date.now() }
        return name
    }

    /** Turning while coming to rest: stand facing the new way (the settle is drawn facing the old one). */
    protected turnedAnimation(facing: string): AnimationDictionaryType {
        if (isIdleVariation(parseAnimationName(this.action)?.action ?? "")) {
            this.pendingIdle = undefined
            this.nextIdleChoice = Date.now() + IDLE_CHOICE_MS
            return `idle-${facing}` as AnimationDictionaryType
        }
        if (parseAnimationName(this.action)?.action === SETTLE) return `idle-${facing}` as AnimationDictionaryType
        return super.turnedAnimation(facing)
    }

    /**
     * A settle starts when it lands only if it is still wanted: the stride it was asked for is still
     * holding its frame, or the blow it follows has played out. Moved, turned or struck again in the
     * meantime, it is kept for next time instead.
     */
    protected adoptsOnLoad(action: AnimationDictionaryType): boolean {
        if (isIdleVariation(parseAnimationName(action)?.action ?? "")) return action === this.pendingIdle && this.action.startsWith("idle-") && parseAnimationName(action)?.facing === this.facingDir
        if (THREATEN_ACTIONS.includes(parseAnimationName(action)?.action ?? "")) return this.attackIntentPose?.action === action
        if (parseAnimationName(action)?.action !== SETTLE) return true
        const pending = this.pendingSettle
        if (pending?.name === action && this.action === pending.stride) {
            this.pendingSettle = undefined
            return true
        }
        return this.awaits(action)
    }

    /** Standing (or as good as): a blow from here is struck from standing. */
    private standing = (): boolean => {
        if (this.action.startsWith("idle-") || isIdleVariation(parseAnimationName(this.action)?.action ?? "") || this.pendingSettle?.stride === this.action) return true
        const parsed = parseAnimationName(this.action)
        if (parsed?.action !== SETTLE) return false
        // Barely out of a blow, the stance is still there to strike again from.
        const settle = this.animations[this.action]?.animation
        return !STANCE_ATTACKS.includes(parsed.after!) || !settle || settle.currentFrame * 2 >= settle.totalFrames
    }

    /**
     * In the air in a jump (game-common/jump.ts): the clip's frame for how far through it the body is,
     * in step with the lift. A leap off an edge holds the legs-reaching-down frame until its drop has
     * landed, and then takes the landing in the knees. A strike thrown in the air plays over it.
     */
    protected jumpPose(entity: Entity): BodyAnimationPose | undefined {
        if (!EMBER_HUMANOIDS[this.spriteName]) return undefined
        const now = Date.now()
        const fall = entity.floorFall
        // Or still in the storey it leapt from, already dropping: this client starts the drop itself.
        const pending = !fall ? predictedFallPending(entity, now) : undefined
        const predicted = pending?.view.leap && predictedFallAirborne(pending, now) ? pending : undefined
        // A drop on the shared curve has come down when this client's clock says so: the server's "landed" is a
        // round trip behind it, and the body waited out that trip on the ground in the legs-reaching-down frame.
        const leaping = !!predicted || (!!fall?.leap && fall.phase !== "landed" && (!fall.trajectory || isAirborne(entity)))
        if (leaping) {
            this.leapFallId = fall?.id ?? predicted.view.id
            this.leapLandedAt = undefined
        } else if (this.leapFallId !== undefined) {
            this.leapFallId = undefined
            this.leapLandedAt = now
        }
        let progress = this.flourishProgress(JUMP_FLOURISH)
        if (leaping) progress = JUMP_HOLD
        else if (this.leapLandedAt !== undefined) {
            const landing = JUMP_LANDING + (now - this.leapLandedAt) / JUMP_MS
            if (landing < 1) progress = landing
            else this.leapLandedAt = undefined
        }
        if (progress === undefined) {
            this.jumpFacing = undefined
            return undefined
        }
        if (this.striking()) return undefined
        this.jumpFacing ??= this.calcDir(entity.movement?.angle ?? this.spriteAngle)
        return { action: `jump-${this.jumpFacing}`, progress }
    }

    /** A generated humanoid lies down as LPC does, and falls with its arms flung up (clips.ts `falling`). */
    protected drawsHeldPose(pose: HeldPose): boolean {
        return pose === "downed" || !!EMBER_HUMANOIDS[this.spriteName]
    }

    /**
     * Drive the full climb cycle by distance along the ladder. Holding a rung
     * freezes the pose; descending or reversing retraces the same frames.
     */
    protected climbPose(entity: Entity): BodyAnimationPose | undefined {
        const rise = ladderRiseOf(entity)
        if (rise === undefined) return undefined
        const frames = EMBER_CLIPS.climb.frames
        const cycle = (((rise / CLIMB_RISE) % 1) + 1) % 1
        return { action: "climb-back", progress: cycle, frame: Math.floor(cycle * frames) % frames }
    }

    protected locomotionAnimation(): AnimationDictionaryType {
        const speed = this.walkPace.speed
        if (this.runKey !== undefined) this.running = this.runKey
        else if (this.running && speed > 0 && speed < WALK_BELOW) this.running = false
        else if (!this.running && speed > RUN_ABOVE) this.running = true
        return `${this.running ? "run" : "walk"}-${this.facingDir}` as AnimationDictionaryType
    }

    /**
     * Aim the next shot along screen heading `heading` (radians, clockwise from screen right): from
     * the bow to where the arrow will land, so the arrow drawn on the bow points the way it flies.
     * Undefined draws the shot along the facing, as it always was.
     */
    aimShot(heading: number | undefined) {
        this.shotHeading = heading
    }

    /** `action`, a shot, drawn along the heading it was aimed at, for a body that aims its shots. */
    private aimed = (action: AnimationDictionaryType): AnimationDictionaryType =>
        this.shotHeading !== undefined && this.attackAction === "shoot" && aimsShots(this.equipment)
            ? (aimedShotName(action, this.shotHeading) as AnimationDictionaryType)
            : action

    protected attackAnimation(): AnimationDictionaryType {
        const [gait] = this.action.split("-")
        const stride = this.animations[this.action]?.animation
        this.resumeGait = undefined
        if (this.settlesAttack(this.attackAction) && this.standing()) {
            this.pendingSettle = undefined
            return `${this.attackAction}.${FROM_REST}-${this.facingDir}` as AnimationDictionaryType
        }
        if (!stride || !MOVING_ATTACK_GAITS.includes(gait) || !(this.attackAction in LPC_ACTION_DURATION_MS)) {
            return this.aimed(super.attackAnimation())
        }
        // Walking or running: strike over the legs from the point of the stride they are at, the
        // legs keeping the pace the body is actually crossing the ground at.
        const phase = stride.currentFrame / stride.totalFrames
        const speed = this.walkPace.speed > 0 ? this.walkPace.speed : undefined
        const strike = this.aimed(movingAttackName(this.attackAction, gait, phase, this.facingDir as Facing, speed) as AnimationDictionaryType)
        this.resumeGait = { action: `${gait}-${this.facingDir}` as AnimationDictionaryType, after: strike }
        return strike
    }

    /**
     * After a strike on the move, straight back into the stride; after a blow struck standing, its
     * settle (for a body that settles); after a settle, idle. Anything else goes on as LPC's does.
     */
    postAnimation = (animation: AnimationDictionaryType): AnimationDictionaryType => {
        const parsed = parseAnimationName(animation)
        if (parsed && isIdleVariation(parsed.action)) return `idle-${parsed.facing}` as AnimationDictionaryType
        if (parsed?.gait) return `${parsed.gait}-${parsed.facing}` as AnimationDictionaryType
        if (parsed?.action === SETTLE) return `idle-${parsed.facing}` as AnimationDictionaryType
        if (parsed && this.settlesAttack(parsed.action)) return `${SETTLE}.${parsed.action}-${parsed.facing}` as AnimationDictionaryType
        return this.standingPostAnimation(animation)
    }

    protected animationStartFrame(action: AnimationDictionaryType, animation?: AnimatedSprite): number {
        // Picking the stride back up after a strike on the move: on from where the strike left the legs.
        const resume = this.resumeGait
        if (resume && animation && action === resume.action) {
            const after = this.baked.get(resume.after)?.gaitPhaseAfter
            this.resumeGait = undefined
            if (after !== undefined) return Math.round(after * animation.totalFrames) % animation.totalFrames
        }
        return super.animationStartFrame(action, animation)
    }

    protected applyPace(action: AnimationDictionaryType, animation?: AnimatedSprite): void {
        super.applyPace(action, animation)
        if (animation && isIdleVariation(parseAnimationName(action)?.action ?? "")) animation.loop = false
        const baked = this.baked.get(action)
        const speed = this.walkPace.speed
        if (!animation || !baked?.strideCycle || speed <= 0) return
        animation.animationSpeed = gaitAnimationSpeed(speed, baked.strideCycle, baked.textures.length)
    }
}
